import { ChangeDetectionStrategy, Component, computed, input, model, output } from '@angular/core';
import { DsButtonModule, DsLoadingModule, DsTooltipDirectiveModule } from '@bmw-ds/components';

import { AddressField } from '../../customer/model/address-field.model';
import { CommunicationField } from '../../customer/model/communication-field.model';
import { Customer } from '../../customer/model/customer.model';
import { IdentityField } from '../../customer/model/identity-field.model';
import { useI18n } from '../../i18n/services/i18n.service';
import { ButtonRowComponent } from '../../modal/button/button-row.component';
import { lensProxy } from '../../utils/lensProxy/lensProxy';
import { ProfileEditContactComponent } from './profile-edit-contact/profile-edit-contact.component';
import { ProfileEditIdentityContainer } from './profile-edit-identity/profile-edit-identity.container';

@Component({
  selector: 'cdf-customer-information-dialog',
  imports: [
    ProfileEditIdentityContainer,
    DsButtonModule,
    ProfileEditContactComponent,
    DsLoadingModule,
    DsTooltipDirectiveModule,
    ButtonRowComponent,
  ],
  template: `
    <form>
      <cdf-profile-edit-identity-container
        [(salutation)]="customerProxy.salutation"
        [(firstName)]="customerProxy.firstName"
        [(lastName)]="customerProxy.lastName"
        [(title)]="customerProxy.title"
        [(middleName)]="customerProxy.middleName"
        [(suffix)]="customerProxy.suffix"
        [(correspondenceLanguage)]="customerProxy.correspondenceLanguage"
        [(taxNumber)]="customerProxy.taxNumber"
        [(taxNumberCountry)]="customerProxy.taxNumberCountry"
        [(nationalId)]="customerProxy.nationalId1"
        [(nationalIdDocumentType)]="customerProxy.nationalId1DocumentType"
        [disabled]="saving()"
        [editingIdentityDisabled]="editingIdentityDisabled()"
        [invalidIdentityFields]="invalidIdentityFields()"
      />
      <div class="contact-columns border-top">
        <cdf-profile-edit-contact
          id="private-contact"
          [(contact)]="customerProxy.privateContact"
          [invalidAddressFields]="invalidPrimaryAddressFields()"
          [invalidCommunicationFields]="invalidPrimaryCommunicationFields()"
          isPrimaryContactData
          [disabled]="saving()"
          [editingEmailDisabled]="editingEmailDisabled()"
          [validationSkipped]="primaryAddressValidationSkipped()"
          (skipAddressValidation)="skipPrimaryAddressValidation.emit()"
        />

        <cdf-profile-edit-contact
          id="business-contact"
          [(contact)]="customerProxy.businessContact"
          [disabled]="saving()"
          [invalidAddressFields]="invalidBusinessAddressFields()"
          [invalidCommunicationFields]="invalidBusinessCommunicationFields()"
          [editingEmailDisabled]="editingEmailDisabled()"
          [validationSkipped]="businessAddressValidationSkipped()"
          (skipAddressValidation)="skipBusinessAddressValidation.emit()"
        />
      </div>
      <ng-content />
      <cdf-button-row class="mt-8x">
        <ds-button ds-loading id="cancel" variant="outline" (click)="closeModal.emit()"
          >{{ i18n('button.cancel') }}
        </ds-button>
        <ds-button
          id="save"
          class="button-padding"
          ds-loading
          [disabled]="savingDisabled()"
          [isLoading]="saving()"
          [ds-tooltip]="i18n('customerInformationDialog.button.resolveIssuesToContinueTooltip')"
          [dsTooltipConfig]="{ disabled: !savingDisabled(), origin: 'bottom', showTriggerDelay: 500 }"
          (click)="saveChanges.emit()"
          >{{ saveButtonLabel() ?? i18n('button.saveChanges') }}
        </ds-button>
      </cdf-button-row>
    </form>
  `,
  styleUrl: './customer-information-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerInformationDialogComponent {
  readonly i18n = useI18n();

  readonly customer = model.required<Customer>();

  readonly saveButtonLabel = input<string>();
  readonly saving = input<boolean>(false);

  readonly invalidPrimaryAddressFields = input<AddressField[]>([]);
  readonly invalidBusinessAddressFields = input<AddressField[]>([]);
  readonly invalidIdentityFields = input<IdentityField[]>([]);
  readonly invalidPrimaryCommunicationFields = input<CommunicationField[]>([]);
  readonly invalidBusinessCommunicationFields = input<CommunicationField[]>([]);
  readonly primaryAddressValidationSkipped = input<boolean>(false);
  readonly businessAddressValidationSkipped = input<boolean>(false);
  readonly editingIdentityDisabled = input(false);
  readonly editingEmailDisabled = input(false);

  readonly saveChanges = output();
  readonly closeModal = output();
  readonly skipPrimaryAddressValidation = output();
  readonly skipBusinessAddressValidation = output();

  protected readonly customerProxy = lensProxy(this.customer);

  protected readonly savingDisabled = computed(
    () =>
      this.invalidPrimaryAddressFields().length > 0 ||
      this.invalidBusinessAddressFields().length > 0 ||
      this.invalidIdentityFields().length > 0 ||
      this.invalidPrimaryCommunicationFields().length > 0 ||
      this.invalidBusinessCommunicationFields().length > 0,
  );
}
